import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

type AnimationHandle = { destroy: () => void; play: () => void; pause: () => void };

/**
 * Lightweight Lottie player.
 * - never runs during SSR (all work happens in useEffect)
 * - lazily imports lottie-web and fetches the JSON only when scrolled into view
 * - pauses when off-screen and respects prefers-reduced-motion
 */
export function Lottie({
  src,
  className,
  loop = true,
  ariaLabel,
}: {
  src: string;
  className?: string;
  loop?: boolean;
  ariaLabel?: string;
}) {
  const host = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = host.current;
    if (!node) return;

    let animation: AnimationHandle | null = null;
    let cancelled = false;
    let started = false;
    // Start the player only once the page has loaded and the browser is idle,
    // so the animation never competes with first paint and interactivity.
    const whenIdle = (fn: () => void) => {
      const idle = () =>
        "requestIdleCallback" in window
          ? window.requestIdleCallback(fn, { timeout: 2500 })
          : (globalThis as typeof globalThis).setTimeout(fn, 300);
      if (document.readyState === "complete") idle();
      else window.addEventListener("load", idle, { once: true });
    };
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    async function load() {
      const [{ default: lottie }, data] = await Promise.all([
        import("lottie-web/build/player/lottie_light_canvas"),
        fetch(src).then((r) => r.json()),
      ]);
      if (cancelled || !node) return;
      animation = lottie.loadAnimation({
        container: node,
        renderer: "canvas",
        rendererSettings: {
          preserveAspectRatio: "xMidYMid meet",
          clearCanvas: true,
          dpr: Math.min(window.devicePixelRatio || 1, 2),
        },
        loop,
        autoplay: !reduceMotion,
        animationData: data,
      }) as unknown as AnimationHandle;
      if (reduceMotion) animation.pause();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        if (visible && !animation && !started) {
          started = true;
          whenIdle(() => void load());
        } else if (animation && !reduceMotion) {
          if (visible) animation.play();
          else animation.pause();
        }
      },
      { rootMargin: "200px 0px" },
    );
    observer.observe(node);

    return () => {
      cancelled = true;
      observer.disconnect();
      animation?.destroy();
    };
  }, [src, loop]);

  return (
    <div
      ref={host}
      className={cn("h-full w-full", className)}
      role="img"
      {...(ariaLabel ? { "aria-label": ariaLabel } : { "aria-hidden": true })}
    />
  );
}
